import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { RequerimentosDiversosMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/requerimentos-diversos-medico/requerimentos-diversos-medico.service';
import { ReportRequerimentosDiversosMedicoRequest, ReportRequerimentosDiversosMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/requerimentos-diversos-medico/requerimentos-diversos-medico.model';
import { RequerimentosDiversosMedicoJobService } from '@/core/services/requerimentos-diversos-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportRequerimentosDiversosMedicoRequest;
type Resp = ReportRequerimentosDiversosMedicoResponse;

/**
 * Controles do formulário: um checkbox binário por opção do checklist (chave dinâmica
 * vinda de `opcoes`), o checkbox `OUTROS` e o campo de texto livre `outrosTexto`.
 */
interface RequerimentosDiversosMedicoFormControls {
    [key: string]: FormControl<boolean | null> | FormControl<string | null>;
    OUTROS: FormControl<boolean | null>;
    outrosTexto: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono "Requerimento de Serviços Diversos - Pessoa Física" (Médico).
 * Herda `RelatorioMedicoBase`: export/polling/download/erro e o card "médico selecionado +
 * Alterar" vêm da base; aqui apenas resolvemos a identificação do médico e montamos as
 * opções do checklist marcadas (`opcoesSelecionadas`) mais o texto opcional de "Outros:".
 */
@Component({
    selector: 'app-requerimentos-diversos-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, InputTextModule, CheckboxModule, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './requerimentos-diversos-medico.html',
    styleUrl: './requerimentos-diversos-medico.scss'
})
export class ReportRequerimentosDiversosMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly requerimentoService = inject(RequerimentosDiversosMedicoService);
    readonly requerimentoJobService = inject(RequerimentosDiversosMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.requerimentoJobService.job$;

    /** Opções do checklist (chave = valor enviado em opcoesSelecionadas; label EXATO do formulário). */
    readonly opcoes: ReadonlyArray<{ key: string; label: string }> = [
        { key: 'CEDULA_IDENTIDADE', label: '2ª Via-Cédula de Identidade Médica' },
        { key: 'PARCELAMENTO', label: 'Parcelamento da(s) anuidade(s) ref.' },
        { key: 'AVERBACAO_NOME', label: 'Averbação do Estado Civil e /ou mudança de nome' },
        { key: 'ATUALIZACAO_ENDERECO', label: 'Atualização de Endereço' },
        { key: 'CERTIDAO_ETICO', label: 'Certidão Ético-Profissional' },
        { key: 'REINSCRICAO', label: 'Reinscrição do Exercício da Medicina' },
        { key: 'REGISTRO_ESPECIALIDADE', label: 'Registro de Qualificação de Especialista em :' },
        { key: 'CANCELAMENTO_INSCRICAO', label: 'Cancelamento de Inscrição por motivo:' },
        { key: 'COMUNICA_EXTRAVIO', label: 'Comunica Extravio/perda de documento' },
        { key: 'CANCELAMENTO_MILITAR', label: 'Cancelamento de Médico Militar' },
        { key: 'APOSTILA_DIPLOMA', label: 'Apostila/anotação em 2ª via de Diploma' },
        { key: 'CERTIFICADO_ESPECIALISTA_RQE', label: '2ª Via Certificado de Especialista (RQE)' }
    ];

    form: FormGroup<RequerimentosDiversosMedicoFormControls> = this.criarFormulario();

    protected get jobFacade(): RelatorioJobFacade {
        return this.requerimentoJobService;
    }

    /** Monta o request a partir da pessoa selecionada e das opções marcadas; null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar o requerimento.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        const opcoesSelecionadas: string[] = [];
        for (const opcao of this.opcoes) {
            if (this.form.controls[opcao.key]?.value) {
                opcoesSelecionadas.push(opcao.key);
            }
        }
        const outrosMarcado = !!this.form.controls.OUTROS.value;
        const outrosTexto = this.form.controls.outrosTexto.value?.trim() || '';
        if (outrosMarcado || outrosTexto) {
            opcoesSelecionadas.push('OUTROS');
        }

        return {
            nuCrm,
            sgUf,
            opcoesSelecionadas,
            ...(outrosTexto ? { outros: outrosTexto } : {})
        };
    }

    protected exportar(request: Req) {
        return this.requerimentoService.exportarPdf(request);
    }

    /** Reseta todas as opções do checklist e o texto de "Outros:" e limpa o job/preview corrente. */
    limparFormulario(): void {
        const reset: Record<string, boolean | string> = { OUTROS: false, outrosTexto: '' };
        for (const opcao of this.opcoes) {
            reset[opcao.key] = false;
        }
        this.form.reset(reset);
        this.limparJob();
    }

    private criarFormulario(): FormGroup<RequerimentosDiversosMedicoFormControls> {
        const controls: RequerimentosDiversosMedicoFormControls = {
            OUTROS: new FormControl<boolean | null>(false),
            outrosTexto: new FormControl<string | null>('')
        };
        for (const opcao of this.opcoes) {
            controls[opcao.key] = new FormControl<boolean | null>(false);
        }
        return new FormGroup<RequerimentosDiversosMedicoFormControls>(controls);
    }
}
